import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ShoppingBag } from 'lucide-react';
import { useCartStore } from '@/store/cartStore';
import { routeTitle } from './AppShell';
import SearchBar from './SearchBar';

/* ============================================================================
   Phone-web (≤768px) top app bar for inner pages: back + route title + search
   + cart. Replaces the logo/search header on those pages via html.m-inner
   (mobile-web.css). Hidden by CSS on tablet/desktop and in the Android app.
   ============================================================================ */

// Tab destinations: title only, no back arrow.
export const MOBILE_TAB_ROOTS = ['/categories', '/design', '/cart', '/account'];

export default function MobileTopBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { itemCount } = useCartStore();
  const [title, setTitle] = useState(() => routeTitle(pathname));
  const [searchOpen, setSearchOpen] = useState(false);
  const isRoot = MOBILE_TAB_ROOTS.includes(pathname);

  // Route title, refined by the page's own <h1> once it renders.
  useEffect(() => {
    const base = routeTitle(pathname);
    setTitle(base === 'EsyPrints' ? '' : base);
    const main = document.querySelector<HTMLElement>('.site-main');
    const update = () => {
      const h1 = main?.querySelector('h1');
      const text = h1?.textContent?.trim();
      if (!h1 || !text || text.length > 40) return;
      setTitle(text);
      // The bar now carries the page name: hide a plain page heading so it
      // isn't shown twice (headings inside bordered cards stay).
      let inCard = false;
      for (let el = h1.parentElement; el && el !== main; el = el.parentElement) {
        if (parseFloat(getComputedStyle(el).borderTopWidth) > 0) { inCard = true; break; }
      }
      if (!inCard) h1.setAttribute('data-in-mbar', '');
    };
    update();
    const mo = main ? new MutationObserver(update) : null;
    mo?.observe(main as HTMLElement, { childList: true, subtree: true, characterData: true });
    const stop = window.setTimeout(() => mo?.disconnect(), 6000);
    return () => { mo?.disconnect(); clearTimeout(stop); };
  }, [pathname]);

  useEffect(() => { setSearchOpen(false); }, [pathname]);
  useEffect(() => {
    if (!searchOpen) return;
    const t = window.setTimeout(() => document.querySelector<HTMLInputElement>('.m-search .search-bar input')?.focus(), 50);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSearchOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); };
  }, [searchOpen]);

  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/'));

  return (
    <>
      <header className="m-topbar">
        {!isRoot && (
          <button type="button" className="m-icon-btn" aria-label="Back" onClick={goBack}>
            <ArrowLeft size={22} />
          </button>
        )}
        <div className={`m-topbar-title ${isRoot ? 'is-root' : ''}`}>{title || <span className="m-brand">Esy<b>Prints</b></span>}</div>
        <button type="button" className="m-icon-btn" aria-label="Search" onClick={() => setSearchOpen(true)}>
          <Search size={21} />
        </button>
        <Link to="/cart" className="m-icon-btn" aria-label={itemCount > 0 ? `Cart, ${itemCount} items` : 'Cart'}>
          <ShoppingBag size={21} />
          {itemCount > 0 && <span className="m-badge">{itemCount > 99 ? '99+' : itemCount}</span>}
        </Link>
      </header>
      {searchOpen && (
        <div className="m-search" role="dialog" aria-modal="true" aria-label="Search">
          <div className="m-search-bar">
            <button type="button" className="m-icon-btn" aria-label="Close search" onClick={() => setSearchOpen(false)}>
              <ArrowLeft size={22} />
            </button>
            <div className="m-search-field"><SearchBar /></div>
          </div>
        </div>
      )}
    </>
  );
}
